import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "pb-4"
}
const _hoisted_2 = { class: "grid grid-cols-2 gap-x-4" }

import { ref, computed, watch } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import { VisuallyHidden } from 'radix-vue';
import { format, parseISO } from 'date-fns';
import { useToast } from 'vue-toastification';
import CustomButton from '@/components/ui/CustomButton.vue';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { queryKeys } from '@/plugins/store/actions/queries/study-periods';
import CustomSelect from '@/components/ui/CustomSelect.vue';
import { store } from '@/plugins/store';
import DatePicker from '@/components/ui/DatePicker.vue';
import CustomInput from '@/components/ui/CustomInput.vue';
import CustomCheckbox from '@/components/ui/CustomCheckbox.vue';
import ApiClient from '@/plugins/store/actions/api/ApiClient';
import { i18n } from '@/plugins/internationalization/i18n';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Barn } from '@/types';

interface CreateTeamState {
    sectionId: number | null;
    name: string;
    startDate: string;
    endDate: string;
    isFavorite: boolean;
    animalGroupId: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CreateTeamModal',
  props: /*@__PURE__*/_mergeModels({
    barn: {}
  }, {
    "modelValue": { type: Boolean },
    "modelModifiers": {},
  }),
  emits: ["update:modelValue"],
  setup(__props: any) {

const props = __props;

const queryClient = useQueryClient();
const toast = useToast();

const showModal = _useModel<boolean>(__props, 'modelValue');

const isLoading = ref(false);

const animalGroupOptions = computed(() => [
    {
        value: 1,
        label: i18n.global.t('views.monitoring.teamOverview.modals.editTeam.fields.animalGroup.options.weaners'),
    },
    {
        value: 2,
        label: i18n.global.t('views.monitoring.teamOverview.modals.editTeam.fields.animalGroup.options.slaughterPigs'),
    },
    {
        value: 3,
        label: i18n.global.t('views.monitoring.teamOverview.modals.editTeam.fields.animalGroup.options.frats'),
    },
]);

function getInitialState() {
    return {
        sectionId: null,
        name: '',
        startDate: new Date().toISOString(),
        endDate: '',
        isFavorite: false,
        animalGroupId: '1',
    };
}

const state = ref<CreateTeamState>(getInitialState());

const sectionOptions = computed(() => store.state.sections?.filter((s) => s.barn_id === props.barn.barn_id) ?? []);

async function handleCreate() {
    try {
        if (isLoading.value) {
            return;
        }

        isLoading.value = true;

        await ApiClient.send(
            'post',
            'monitor/study-periods',
            {
                skipHandleError: true,
            },
            {
                location_id: state.value.sectionId,
                name: state.value.name,
                start: format(parseISO(state.value.startDate), 'yyyy-MM-dd'),
                end: state.value.endDate ? format(parseISO(state.value.endDate), 'yyyy-MM-dd') : null,
                is_favorite: state.value.isFavorite,
                animal_group_id: Number(state.value.animalGroupId),
            }
        );

        await queryClient.invalidateQueries({ queryKey: queryKeys.getStudyPeriodsRoot });

        showModal.value = false;

        toast.success(i18n.global.t('views.monitoring.teamOverview.modals.createTeam.successMessages.teamWasCreated'));
    } catch (err) {
        const errorMessage = (err as any)?.data?.conflicts?.[0]?.conflict_message || i18n.global.t('views.monitoring.teamOverview.modals.createTeam.errors.couldNotCreateTeam');
        toast.error(errorMessage);
    } finally {
        isLoading.value = false;
    }
}

function handleSectionChange(value: number | null) {
    const section = sectionOptions.value.find((s) => s.id === value);

    if (!section) {
        return;
    }

    const barn = store.state.barns?.find((b) => b.barn_id === section.barn_id);

    if (!barn) {
        return;
    }

    state.value.animalGroupId = barn.animal_group_id.toString();
}

function resetForm() {
    state.value = getInitialState();
}

const isFormValid = computed(() => state.value.sectionId !== null && state.value.name.length > 0);

watch(showModal, (value) => {
    if (!value) {
        setTimeout(() => {
            resetForm();
        }, 300);
    } else {
        state.value.animalGroupId = props.barn.animal_group_id.toString();
    }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Dialog), {
    modelValue: showModal.value,
    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((showModal).value = $event)),
    disabled: isLoading.value,
    class: "sm:max-w-[540px]"
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(DialogHeader), null, {
        default: _withCtx(() => [
          _createVNode(_unref(DialogTitle), null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamOverview.modals.createTeam.title')), 1)
            ]),
            _: 1
          }),
          _createVNode(_unref(VisuallyHidden), null, {
            default: _withCtx(() => [
              _createVNode(_unref(DialogDescription), { class: "pt-4" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamOverview.modals.createTeam.description')), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createElementVNode("form", {
        class: "mt-4 space-y-2",
        onSubmit: _withModifiers(handleCreate, ["prevent"])
      }, [
        _createVNode(CustomSelect, {
          value: state.value.sectionId,
          "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((state.value.sectionId) = $event)),
          items: sectionOptions.value,
          rules: [{ validate: (value) => value !== null, message: '' }],
          "item-text": "name",
          "item-value": "id",
          label: _ctx.$t('views.monitoring.teamOverview.modals.createTeam.fields.section'),
          required: "",
          "allow-empty": false,
          "onUpdate:modelValue": handleSectionChange
        }, null, 8, ["value", "items", "rules", "label"]),
        _createVNode(CustomInput, {
          value: (state.value.name as any),
          "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => (((state.value.name as any)) = $event)),
          rules: [{ validate: (value) => value.length > 0, message: '' }],
          required: "",
          label: _ctx.$t('views.monitoring.teamOverview.modals.createTeam.fields.name')
        }, null, 8, ["value", "rules", "label"]),
        (_ctx.barn.animal_group_id === 3)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createVNode(_unref(RadioGroup), {
                modelValue: state.value.animalGroupId,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((state.value.animalGroupId) = $event)),
                class: "gap-x-5"
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(animalGroupOptions.value, (option) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: option.value,
                      class: "flex items-center cursor-pointer"
                    }, [
                      _createVNode(_unref(RadioGroupItem), {
                        id: `option-id-${option.value}`,
                        value: option.value.toString()
                      }, null, 8, ["id", "value"]),
                      _createVNode(_unref(Label), {
                        for: `option-id-${option.value}`,
                        class: "font-normal cursor-pointer pl-2"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(option.label), 1)
                        ]),
                        _: 2
                      }, 1032, ["for"])
                    ]))
                  }), 128))
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(DatePicker, {
            value: state.value.startDate,
            "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((state.value.startDate) = $event)),
            required: "",
            label: _ctx.$t('views.monitoring.teamOverview.modals.createTeam.fields.startDate')
          }, null, 8, ["value", "label"]),
          _createVNode(DatePicker, {
            value: state.value.endDate,
            "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((state.value.endDate) = $event)),
            resetable: "",
            label: _ctx.$t('views.monitoring.teamOverview.modals.createTeam.fields.endDate')
          }, null, 8, ["value", "label"])
        ]),
        _createVNode(CustomCheckbox, {
          checked: state.value.isFavorite,
          "onUpdate:checked": _cache[5] || (_cache[5] = ($event: any) => ((state.value.isFavorite) = $event)),
          label: _ctx.$t('views.monitoring.teamOverview.modals.createTeam.fields.markAsFavorite'),
          class: "-ml-1"
        }, null, 8, ["checked", "label"])
      ], 32),
      _createVNode(_unref(DialogFooter), { class: "mt-2" }, {
        default: _withCtx(() => [
          _createVNode(_unref(DialogClose), { "as-child": "" }, {
            default: _withCtx(() => [
              _createVNode(CustomButton, {
                disabled: isLoading.value,
                "color-preset": "white"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamOverview.modals.createTeam.buttons.cancel')), 1)
                ]),
                _: 1
              }, 8, ["disabled"])
            ]),
            _: 1
          }),
          _createVNode(CustomButton, {
            loading: isLoading.value,
            disabled: !isFormValid.value,
            onClick: handleCreate
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamOverview.modals.createTeam.buttons.create')), 1)
            ]),
            _: 1
          }, 8, ["loading", "disabled"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue", "disabled"]))
}
}

})